import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { registerAccount, registerUser } from "../../features/user/userSlice";
import { base_url } from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";

const MemberSignUp = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [packages, setPackages] = useState([]);
  const [referredUserData, setReferredUserData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const timeoutRef = useRef(null);
  const [useSameAddress, setUseSameAddress] = useState(false);

  // Debounce function for referral code validation
  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch referred user data
  const fetchReferredUserData = async (referralCode) => {
    if (!referralCode) {
      setReferredUserData(null);
      return;
    }
    try {
      const response = await axios.post(
        `${base_url}v1/user/verifyReferralCode`,
        {
          referralCode,
        },
      );
      setReferredUserData(response.data.data);
    } catch (error) {
      console.error("Error fetching referred user data:", error);
      setReferredUserData(null);
    }
  };

  const debouncedFetchReferredUserData = debounce(fetchReferredUserData, 500);

  // Fetch packages for the commented-out package selection step
  useEffect(() => {
    if (currentStep === 3 && show) {
      axios
        .get(`${base_url}v1/package/fetchAllpackage`)
        .then((response) => {
          setPackages(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching packages:", error);
        });
    }
  }, [currentStep, show]);

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate max date for 18+ validation (today minus 18 years)
  const maxDate18 = new Date();
  maxDate18.setFullYear(maxDate18.getFullYear() - 18);

  // Validation schemas for each step
  const signupSchemas = [
    Yup.object({
      name: Yup.string().required("Full Name is required"),
      dob: Yup.date()
        .required("DOB is required")
        .max(maxDate18, "You must be 18+ years of age"),
      gender: Yup.string().required("Gender is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string()
        .matches(/^[0-9]+$/, "Not a Valid Phone Number")
        .required("Phone is required"),
      bloodGroup: Yup.string().required("Blood Group is required"),
      profilePhoto: Yup.mixed().required("Profile photo is required"),
      resAddress_line: Yup.string().required("Residential Address is required"),
      resCountry: Yup.string().required("Residential Country is required"),
      resState: Yup.string().required("Residential State is required"),
      resCity: Yup.string().required("Residential City is required"),
      resDistrict: Yup.string().required("Residential District is required"),
      resPincode: Yup.string().required("Residential Pincode is required"),
      correAddress_line: Yup.string().required(
        "Correspondence/Office Address is required",
      ),
      correCountry: Yup.string().required(
        "Correspondence/Office Country is required",
      ),
      correState: Yup.string().required(
        "Correspondence/Office State is required",
      ),
      correCity: Yup.string().required(
        "Correspondence/Office City is required",
      ),
      correDistrict: Yup.string().required(
        "Correspondence/Office District is required",
      ),
      correPincode: Yup.string().required(
        "Correspondence/Office Pincode is required",
      ),
    }),
    Yup.object({
      referralCode: Yup.string(),
      termsAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the Terms and Conditions",
      ),
      declarationAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the Declaration",
      ),
    }),
  ];

  const initialSignupValues = {
    name: "",
    dob: "",
    gender: "",
    email: "",
    mobile: "",
    bloodGroup: "",
    profilePhoto: null,
    resAddress_line: "",
    resCountry: "",
    resState: "",
    resCity: "",
    resDistrict: "",
    resPincode: "",
    correAddress_line: "",
    correCountry: "",
    correState: "",
    correCity: "",
    correDistrict: "",
    correPincode: "",
    referralCode: "",
    termsAccepted: false,
    declarationAccepted: false,
  };

  const steps = ["Personal Information", "Reference"];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="modal-90w md:modal-lg"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          {!isRegistered && (
            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                Member Sign Up
              </h3>
              <p className="text-gray-500 text-xs md:text-sm">
                Fill in your details to sign up as a Member
              </p>
            </div>
          )}
          {isRegistered ? (
            <div className="text-center py-6">
              <h2 className="text-lg md:text-xl font-bold text-green-600">
                Registration Successful
              </h2>
              <svg
                className="w-10 h-10 mx-auto text-green-600 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <>
              <Formik
                initialValues={initialSignupValues}
                validationSchema={signupSchemas[currentStep]}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  if (currentStep < steps.length - 1) {
                    setCurrentStep((prev) => prev + 1);
                    setSubmitting(false);
                  } else {
                    try {
                      const formData = new FormData();
                      formData.append("name", values.name);
                      formData.append("dob", values.dob);
                      formData.append("gender", values.gender);
                      formData.append("email", values.email);
                      formData.append("mobile", values.mobile);
                      formData.append("bloodGroup", values.bloodGroup);
                      formData.append("address_line", values.resAddress_line);
                      formData.append("country", values.resCountry);
                      formData.append("state", values.resState);
                      formData.append("city", values.resCity);
                      formData.append("district", values.resDistrict);
                      formData.append("pincode", values.resPincode);
                      formData.append(
                        "correAddress_line",
                        values.correAddress_line,
                      );
                      formData.append("correCountry", values.correCountry);
                      formData.append("correState", values.correState);
                      formData.append("correCity", values.correCity);
                      formData.append("correDistrict", values.correDistrict);
                      formData.append("correPincode", values.correPincode);
                      if (values.referralCode) {
                        formData.append("referralCode", values.referralCode);
                      }
                      formData.append("profilePhoto", values.profilePhoto);

                      await dispatch(registerUser(formData)).unwrap();
                      setIsRegistered(true);
                      resetForm();
                      setTimeout(() => {
                        onHide();
                        toast.success(
                          "Registration successful! Please sign in.",
                        );
                        navigate("https://lifeonplus.in/signin/member");
                      }, 2000);
                    } catch (error) {
                      console.error("Registration error:", error);
                      toast.error(
                        error.response?.data?.message || "Registration failed",
                      );
                    } finally {
                      setSubmitting(false);
                    }
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="flex flex-col gap-3">
                    {currentStep === 0 && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-10 gap-3">
                          {/* Profile */}
                          <div className="md:col-span-2 flex justify-center">
                            <div className="flex flex-col items-center">
                              <div
                                className="w-24 h-24 md:w-28 md:h-28 border border-gray-300 rounded-md overflow-hidden cursor-pointer"
                                onClick={() =>
                                  document
                                    .getElementById("profilePhoto")
                                    .click()
                                }
                              >
                                {previewImage ? (
                                  <img
                                    src={previewImage}
                                    alt="Profile Preview"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                    <span className="text-[11px] text-gray-500 text-center px-2">
                                      Upload Profile
                                    </span>
                                  </div>
                                )}
                              </div>

                              <input
                                type="file"
                                id="profilePhoto"
                                className="hidden"
                                accept="image/*"
                                onChange={(event) => {
                                  const file = event.target.files[0];
                                  setFieldValue("profilePhoto", file);

                                  if (file) {
                                    setPreviewImage(URL.createObjectURL(file));
                                  } else {
                                    setPreviewImage(null);
                                  }
                                }}
                              />

                              <ErrorMessage
                                name="profilePhoto"
                                component="div"
                                className="text-red-500 text-[10px] mt-1 text-center"
                              />
                            </div>
                          </div>

                          {/* Basic Details */}
                          <div className="md:col-span-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Full Name
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="name"
                                  placeholder="Enter Full Name"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="name"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Phone Number
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="mobile"
                                  placeholder="Enter Phone Number"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="mobile"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Email<span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="email"
                                  name="email"
                                  placeholder="Enter Email"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="email"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Blood Group
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  as="select"
                                  name="bloodGroup"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                >
                                  <option value="">Select Blood Group</option>
                                  <option value="A+">A+</option>
                                  <option value="A-">A-</option>
                                  <option value="B+">B+</option>
                                  <option value="B-">B-</option>
                                  <option value="AB+">AB+</option>
                                  <option value="AB-">AB-</option>
                                  <option value="O+">O+</option>
                                  <option value="O-">O-</option>
                                </Field>
                                <ErrorMessage
                                  name="bloodGroup"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Date of Birth
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="date"
                                  name="dob"
                                  max={maxDate18.toISOString().split("T")[0]}
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="dob"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Gender<span className="text-red-500">*</span>
                                </label>
                                <Field
                                  as="select"
                                  name="gender"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                >
                                  <option value="">Select Gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                  <option value="other">Other</option>
                                </Field>
                                <ErrorMessage
                                  name="gender"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-2">
                          <h3 className="text-sm font-semibold mb-2">
                            Residential Address{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Street Address{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resAddress_line"
                                placeholder="Enter Street Address"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resAddress_line"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                City <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resCity"
                                placeholder="Enter City"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resCity"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                District <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resDistrict"
                                placeholder="Enter District"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resDistrict"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Pincode <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resPincode"
                                placeholder="Enter Pincode"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resPincode"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Country <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resCountry"
                                placeholder="Enter Country"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resCountry"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                State <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resState"
                                placeholder="Enter State"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resState"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                          </div>
                        </div>
                        {/* Checkbox */}
                        <div className="mt-1">
                          <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useSameAddress}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setUseSameAddress(checked);
                                if (checked) {
                                  setFieldValue(
                                    "correAddress_line",
                                    values.resAddress_line || "",
                                  );
                                  setFieldValue(
                                    "correCountry",
                                    values.resCountry || "",
                                  );
                                  setFieldValue(
                                    "correState",
                                    values.resState || "",
                                  );
                                  setFieldValue(
                                    "correCity",
                                    values.resCity || "",
                                  );
                                  setFieldValue(
                                    "correDistrict",
                                    values.resDistrict || "",
                                  );
                                  setFieldValue(
                                    "correPincode",
                                    values.resPincode || "",
                                  );
                                } else {
                                  // Optional: clear correspondence fields when unchecked
                                  setFieldValue("correAddress_line", "");
                                  setFieldValue("correCountry", "");
                                  setFieldValue("correState", "");
                                  setFieldValue("correCity", "");
                                  setFieldValue("correDistrict", "");
                                  setFieldValue("correPincode", "");
                                }
                              }}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
                            />
                            <span>
                              Same as Residential Address (for Correspondence /
                              Office Address)
                            </span>
                          </label>
                        </div>
                        <div className="mt-1">
                          <h3 className="text-sm font-semibold mb-2">
                            Correspondence / Office Address{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Street Address{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correAddress_line"
                                placeholder="Enter Street Address"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correAddress_line"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                City <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correCity"
                                placeholder="Enter City"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correCity"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                District <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correDistrict"
                                placeholder="Enter District"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correDistrict"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Pincode <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correPincode"
                                placeholder="Enter Pincode"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correPincode"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Country <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correCountry"
                                placeholder="Enter Country"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correCountry"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                State <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correState"
                                placeholder="Enter State"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correState"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 1 && (
                      <>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Referral Code
                            </label>
                            <Field
                              type="text"
                              name="referralCode"
                              placeholder="Enter Referral Code"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                              onChange={(e) => {
                                setFieldValue("referralCode", e.target.value);
                                debouncedFetchReferredUserData(e.target.value);
                              }}
                            />
                            <ErrorMessage
                              name="referralCode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        {referredUserData ? (
                          <>
                            <h2 className="text-lg md:text-xl font-medium">
                              Upline Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Full Name
                                </label>
                                <input
                                  type="text"
                                  value={referredUserData.name || ""}
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Phone Number
                                </label>
                                <input
                                  type="text"
                                  value={
                                    referredUserData.mobile ||
                                    referredUserData.phone ||
                                    ""
                                  }
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {values.referralCode && (
                              <div className="text-red-500 text-xs mt-1">
                                Invalid Referral Code
                              </div>
                            )}
                          </>
                        )}
                        <div className="mt-4">
                          <h2 className="text-lg md:text-xl font-medium">
                            Terms and Conditions
                          </h2>
                          <div className="max-h-40 overflow-y-auto border border-gray-300 p-3 rounded-md bg-gray-50 text-sm">
                            <p className="mb-3 font-medium">
                              LifeOnPlus Membership Terms & Conditions
                              <br />
                              Effective Date: 6th August 2026
                              <br />
                              Company: LifeOnPlus
                              <br />
                              Registered Office: Mumbai, Maharashtra, India
                              <br />
                              Jurisdiction: Courts at Bengaluru (Bangalore),
                              Karnataka, India
                            </p>

                            <ol className="list-decimal ml-4 space-y-3">
                              <li>
                                <strong>Acceptance of Terms</strong>
                                <br />
                                By enrolling in any LifeOnPlus membership
                                package, purchasing any membership plan, or
                                using any LifeOnPlus products or services, the
                                Member confirms that they have read, understood,
                                and agreed to these Terms & Conditions, Privacy
                                Policy, Disclaimer, and all applicable company
                                policies. These Terms shall be binding upon the
                                Member and their legal representatives.
                              </li>

                              <li>
                                <strong>Eligibility</strong>
                                <br />
                                A Member shall be eligible only if:
                                <br />
                                • He/She is at least 18 years of age.
                                <br />
                                • Possesses valid Government-issued Identity
                                Proof and KYC documents.
                                <br />
                                • Provides accurate and complete personal
                                information.
                                <br />
                                • Maintains updated contact details, PAN,
                                Aadhaar (where applicable), and bank details for
                                payouts.
                                <br />
                                • Is legally competent to enter into a contract
                                under applicable Indian laws.
                                <br />
                                LifeOnPlus reserves the right to reject or
                                cancel any membership if eligibility
                                requirements are not fulfilled.
                              </li>

                              <li>
                                <strong>Membership Packages</strong>
                                <br />
                                Membership benefits shall depend solely on the
                                package purchased. Each package may contain:
                                Digital Emergency Card, Physical Emergency Card,
                                Medical Benefits, Complimentary Health Tests,
                                Digital Services, One Touch Emergency Services,
                                Family Benefits (where applicable), Partner
                                Benefits, Discount Programs, and other services
                                specifically mentioned in the purchased package.
                                LifeOnPlus reserves the right to modify,
                                upgrade, replace, discontinue, or improve any
                                package benefits without prior notice wherever
                                necessary due to operational, legal,
                                technological, or regulatory reasons.
                              </li>

                              <li>
                                <strong>Membership Validity</strong>
                                <br />
                                Membership shall remain valid only for the
                                period mentioned in the purchased package.
                                Benefits automatically expire after the validity
                                period unless renewed. Expired members shall not
                                be entitled to complimentary medical tests,
                                emergency services, discounts, rewards, or
                                package benefits.
                              </li>

                              <li>
                                <strong>Complimentary Medical Tests</strong>
                                <br />
                                Complimentary medical tests are subject to the
                                following conditions: The Member must possess an
                                active and valid membership. Tests shall only be
                                available within the serviceable geographical
                                locations notified by LifeOnPlus. Members must
                                visit the designated location within the
                                stipulated date and time. Missed appointments
                                shall not be rescheduled unless permitted by the
                                service partner. Complimentary tests cannot be
                                exchanged for cash or transferred. Availability
                                depends upon partner availability, city
                                coverage, operational feasibility, and
                                Government regulations.
                              </li>

                              <li>
                                <strong>Medical Services Disclaimer</strong>
                                <br />
                                LifeOnPlus does not itself perform pathological
                                or diagnostic testing.
                                <br />
                                <strong>Invasive Tests:</strong> Blood tests and
                                invasive diagnostic procedures shall be
                                conducted solely by NABL Accredited
                                Laboratories, NABH Hospitals, Government
                                Approved Laboratories, or other legally
                                authorized diagnostic partners.
                                <br />
                                <strong>Non-Invasive Tests:</strong>{" "}
                                Non-invasive screenings may be conducted by
                                LifeOnPlus, Authorized Franchisees, Authorized
                                Healthcare Partners, or Approved Service
                                Providers.
                              </li>

                              <li>
                                <strong>
                                  No Responsibility for Medical Reports
                                </strong>
                                <br />
                                LifeOnPlus acts only as a facilitator. Medical
                                reports are prepared solely by diagnostic
                                laboratories. Report interpretation is the
                                responsibility of the consulting doctor.
                                LifeOnPlus does not guarantee the correctness,
                                accuracy, diagnosis, treatment recommendation,
                                or medical outcome. Members are advised to
                                consult qualified medical practitioners before
                                taking any medical decision. LifeOnPlus shall
                                not be liable for wrong diagnosis, delayed
                                reports, incorrect reports, laboratory
                                negligence, medical complications, staff
                                behaviour, laboratory hygiene, doctor opinions,
                                or treatment outcomes.
                              </li>

                              <li>
                                <strong>
                                  Emergency & One Touch Services Disclaimer
                                </strong>
                                <br />
                                LifeOnPlus provides technology-enabled emergency
                                assistance. However, service availability
                                depends upon internet availability, GPS
                                accuracy, mobile handset, telecom operators, SMS
                                gateways, third-party APIs, Google Maps, mobile
                                operating systems, and cloud servers. LifeOnPlus
                                shall not be responsible if emergency alerts
                                fail due to no network coverage, mobile switched
                                off, low battery, GPS disabled, app permissions
                                disabled, server downtime, telecom failure,
                                third-party failure, natural calamities, or
                                force majeure events.
                              </li>

                              <li>
                                <strong>Panic Button SMS Limitation</strong>
                                <br />
                                Emergency SMS alerts shall be limited according
                                to the membership package. Once the allocated
                                SMS quota is exhausted, additional SMS services
                                may be purchased. Applicable recharge charges
                                shall be communicated by the company. No free
                                unlimited SMS facility shall be provided unless
                                specifically included in the purchased package.
                              </li>

                              <li>
                                <strong>Mobile Application Disclaimer</strong>
                                <br />
                                LifeOnPlus continuously upgrades and maintains
                                its application. However, temporary
                                interruptions may occur due to app updates,
                                software bugs, maintenance, server issues,
                                internet disruptions, third-party integrations,
                                or mobile device compatibility. LifeOnPlus shall
                                not be responsible for any direct or indirect
                                losses arising due to temporary application
                                failure, login issues, payment gateway
                                interruptions, notification delays, third-party
                                API failures, or mobile OS incompatibility.
                              </li>

                              <li>
                                <strong>Products & Services Warranty</strong>
                                <br />
                                LifeOnPlus shall provide support for its own
                                products and services for a period of One (1)
                                Year from the date of activation, unless
                                otherwise specified. Where technically feasible,
                                the company shall endeavour to resolve reported
                                issues within 48 to 72 working hours. Resolution
                                timelines may vary due to third-party
                                dependencies, public holidays, force majeure,
                                remote locations, or regulatory restrictions.
                              </li>

                              <li>
                                <strong>Referral & Business Promotion</strong>
                                <br />
                                Members may refer LifeOnPlus products and
                                memberships. However: LifeOnPlus is{" "}
                                <strong>NOT</strong> a Network Marketing (MLM)
                                Company. Membership does not create an
                                employment relationship. Members are not
                                employees, agents, or legal representatives of
                                LifeOnPlus unless specifically appointed.
                                Members shall never promise fixed income,
                                guarantee earnings, make false medical claims,
                                misrepresent products, use misleading
                                advertisements, conduct unauthorized seminars,
                                or collect money on behalf of the company
                                without written authorization.
                              </li>

                              <li>
                                <strong>
                                  Referral Income (Where Applicable)
                                </strong>
                                <br />
                                Where referral incentives are offered, income
                                shall be credited only upon successful
                                membership activation, successful sale,
                                successful onboarding, and fulfillment of
                                company policies. Applicable deductions include
                                TDS, GST, Service Charges, Government Taxes, and
                                other statutory deductions. LifeOnPlus reserves
                                the right to revise incentive structures without
                                prior notice.
                              </li>

                              <li>
                                <strong>Commission Withdrawal Policy</strong>
                                <br />
                                Minimum withdrawal amount: ₹200.
                                <br />
                                Applicable deductions: TDS – 2%, TDS – 20% if
                                PAN is not submitted (as per Income Tax Act),
                                Service Charge – 10%, GST @ 18% on applicable
                                service charges, Wallet retention policy
                                (currently up to 40% or as notified by the
                                company). Processing time may vary depending on
                                banking systems.
                              </li>

                              <li>
                                <strong>Digital Wallet</strong>
                                <br />
                                LifeOnPlus may maintain a Member Wallet. Wallet
                                balances cannot be treated as bank deposits,
                                carry no interest, may be utilized only
                                according to company policies, and cannot be
                                transferred without company approval.
                              </li>

                              <li>
                                <strong>Membership Fees</strong>
                                <br />
                                Membership fees are non-refundable,
                                non-transferable, non-adjustable, and
                                non-cancellable unless specifically required
                                under applicable law.
                              </li>

                              <li>
                                <strong>Refund Policy</strong>
                                <br />
                                No refund shall be issued after successful
                                membership activation. Refunds shall not be
                                provided due to change of mind, non-usage,
                                relocation, failure to avail services, missed
                                appointments, or expiry of membership.
                              </li>

                              <li>
                                <strong>Add-On Services</strong>
                                <br />
                                Certain services are chargeable. Example:
                                Digital Card or Physical Card background change
                                – ₹100 + Applicable GST. Additional services
                                shall be charged according to the prevailing
                                price list.
                              </li>

                              <li>
                                <strong>Data Privacy</strong>
                                <br />
                                LifeOnPlus may collect Name, Address, Mobile
                                Number, Email, PAN, Aadhaar (where legally
                                permitted), Medical details (limited), Banking
                                information, and Location (where consented). The
                                information shall be used solely for membership
                                management, customer support, medical
                                coordination, reward processing, legal
                                compliance, and payment processing. LifeOnPlus
                                shall take reasonable security measures to
                                protect personal information.
                              </li>

                              <li>
                                <strong>Intellectual Property</strong>
                                <br />
                                All logos, trademarks, software, mobile
                                applications, designs, membership cards, and
                                marketing material remain the exclusive property
                                of LifeOnPlus. Unauthorized copying,
                                modification, or reproduction is prohibited.
                              </li>

                              <li>
                                <strong>Member Responsibilities</strong>
                                <br />
                                Members shall provide accurate information, keep
                                passwords confidential, update KYC details,
                                cooperate during verification, follow lawful
                                conduct, and use services responsibly.
                              </li>

                              <li>
                                <strong>Prohibited Activities</strong>
                                <br />
                                Members shall not commit fraud, use fake
                                documents, impersonate another person, misuse
                                emergency services, abuse customer support,
                                upload malicious software, attempt hacking,
                                defame the company, spread false information, or
                                use membership for unlawful purposes.
                              </li>

                              <li>
                                <strong>Suspension & Termination</strong>
                                <br />
                                LifeOnPlus may suspend or terminate membership
                                without notice for fraud, misrepresentation,
                                illegal activities, fake KYC, chargebacks, abuse
                                of services, violation of company policies, or
                                non-compliance with applicable laws. Termination
                                shall not entitle the Member to any refund.
                              </li>

                              <li>
                                <strong>Force Majeure</strong>
                                <br />
                                LifeOnPlus shall not be liable for delays or
                                non-performance due to events beyond its
                                reasonable control, including but not limited to
                                natural disasters, floods, earthquakes, fire,
                                pandemic, government restrictions, internet
                                outages, cyber attacks, labour strikes, war, or
                                civil unrest.
                              </li>

                              <li>
                                <strong>Limitation of Liability</strong>
                                <br />
                                To the maximum extent permitted by law,
                                LifeOnPlus shall not be liable for indirect
                                damages, consequential damages, loss of income,
                                medical negligence by partners, mental distress,
                                loss of opportunity, data loss caused by third
                                parties, mobile device failure, or network
                                failures. The total liability of LifeOnPlus, if
                                any, shall not exceed the membership fee paid by
                                the Member for the applicable package.
                              </li>

                              <li>
                                <strong>Indemnity</strong>
                                <br />
                                The Member agrees to indemnify and hold harmless
                                LifeOnPlus, its directors, employees,
                                affiliates, service providers, and partners
                                against any claims, damages, losses,
                                liabilities, costs, or expenses arising out of
                                breach of these Terms & Conditions, misuse of
                                services, false representations made by the
                                Member, or violation of applicable laws.
                              </li>

                              <li>
                                <strong>Governing Law</strong>
                                <br />
                                These Terms shall be governed by the laws of
                                India.
                              </li>

                              <li>
                                <strong>Jurisdiction</strong>
                                <br />
                                Any dispute arising out of or relating to these
                                Terms & Conditions shall be subject to the
                                exclusive jurisdiction of the competent courts
                                at Bengaluru (Bangalore), Karnataka,
                                irrespective of the location of the Member or
                                the registered office of the Company.
                              </li>

                              <li>
                                <strong>Amendments</strong>
                                <br />
                                LifeOnPlus reserves the right to modify, revise,
                                update, or replace these Terms & Conditions at
                                any time. The latest version published by the
                                company shall prevail.
                              </li>

                              <li>
                                <strong>Entire Agreement</strong>
                                <br />
                                These Terms & Conditions constitute the complete
                                agreement between the Member and LifeOnPlus
                                regarding membership and supersede all previous
                                verbal or written understandings.
                              </li>

                              <li>
                                <strong>Member Declaration</strong>
                                <br />I hereby declare that: I have read and
                                understood all Terms & Conditions. I voluntarily
                                enroll in the LifeOnPlus Membership. I
                                understand that medical services are provided by
                                authorized partners. I understand that
                                LifeOnPlus is a facilitator and technology
                                service provider. I understand that
                                complimentary benefits are subject to package
                                eligibility, availability, time, and location. I
                                agree that membership fees are non-refundable
                                except where required by law. I agree to comply
                                with all company policies and applicable laws. I
                                understand that LifeOnPlus is not responsible
                                for third-party laboratory results, network
                                failures, telecom issues, or delays beyond its
                                control. I consent to the collection and use of
                                my information for membership administration,
                                payouts (where applicable), and service
                                delivery. I accept these Terms & Conditions in
                                full without reservation.
                              </li>
                            </ol>
                          </div>

                          <div className="mt-2 flex items-center">
                            <Field
                              type="checkbox"
                              name="termsAccepted"
                              className="h-4 w-4 text-[#00C70D] border-gray-300 rounded cursor-pointer"
                            />
                            <label className="ml-2 text-sm">
                              I have read and accept the Terms and Conditions
                            </label>
                          </div>
                          <ErrorMessage
                            name="termsAccepted"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div className="mt-4">
                          <h2 className="text-lg md:text-xl font-medium">
                            Declaration
                          </h2>
                          <div className="border border-gray-300 p-3 rounded-md bg-gray-50 text-sm">
                            <p>
                              I, {values.name || "_________"}, declare that the
                              information provided is true and correct. I have
                              read and understood the Terms & Conditions of
                              LifeOnPlus and agree to abide by them.
                            </p>
                          </div>
                          <div className="mt-2 flex items-center">
                            <Field
                              type="checkbox"
                              name="declarationAccepted"
                              className="h-4 w-4 text-[#00C70D] border-gray-300 rounded cursor-pointer"
                            />
                            <label className="ml-2 text-sm">
                              I accept the Declaration
                            </label>
                          </div>
                          <ErrorMessage
                            name="declarationAccepted"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      </>
                    )}
                    <div className="mt-6 flex justify-center gap-3">
                      {currentStep > 0 && (
                        <button
                          type="button"
                          className="w-28 md:w-36 py-2 bg-gray-300 text-gray-800 font-semibold text-sm md:text-base rounded-md shadow-md hover:bg-gray-400"
                          onClick={() => setCurrentStep((prev) => prev - 1)}
                        >
                          Back
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md flex items-center justify-center ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 md:h-5 md:w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Loading...
                          </>
                        ) : currentStep < steps.length - 1 ? (
                          "Continue"
                        ) : (
                          "Register"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MemberSignUp;
